import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppAdoptionAnalyticsHelper } from '@core/Analytics/AppAdoptionAnalyticsHelper';
import {
    AppAdoptionInvitationsResult,
    AppAdoptionService,
    CollaboratorAdoptionEntry,
} from '@services/AppAdoptionService';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { Description } from '@shared/utils/DescriptionsHelper';

export type AdoptionStatus = 'installed' | 'invited' | 'notInstalled' | 'noWhatsApp';
export type AdoptionStatusFilter = 'all' | 'withApp' | 'withoutApp' | 'invited';
export type AdoptionPlatformFilter = 'all' | 'android' | 'ios';
export type AdoptionPlatform = 'android' | 'ios';

export interface AppAdoptionTableRow {
    userID: string;
    name: string;
    imageURL?: string;
    /** Display phone (whatsapp: prefix stripped, leading + guaranteed) or ''. */
    phoneLabel: string;
    empresa: string;
    status: AdoptionStatus;
    statusLabel: string;
    /** "vence en N días" while an invitation is live; '' otherwise. */
    expiryLabel: string;
    platform: AdoptionPlatform | null;
    platformLabel: string;
    /** "v2.4.1" or ''. */
    appVersionLabel: string;
    /** As reported by the app ("Samsung Galaxy A54") or ''. */
    deviceModelLabel: string;
    /** "Android 14" / "iOS 17.5" or ''. */
    osLabel: string;
    installedLabel: string;
    lastActivityLabel: string;
    /** Relative and absolute together, for the device detail ("3 días atrás · 29/8/2026, 09:12"). */
    lastActivityDetailLabel: string;
    /** Whether an invitation can usefully reach this collaborator right now. */
    canInvite: boolean;
    searchValue: string;
}

export interface AdoptionCounts {
    total: number;
    withApp: number;
    withoutApp: number;
    invited: number;
}

export interface SendInvitationsResult {
    kind: 'success' | 'error';
    message: string;
}

export const adoptionStatusDotColors: Record<AdoptionStatus, string> = {
    installed: designSystemStyles.colorStatusSuccess,
    invited: designSystemStyles.colorStatusActive,
    notInstalled: designSystemStyles.colorStatusNeutral,
    noWhatsApp: designSystemStyles.colorStatusError,
};

export const getAdoptionStatus = (entry: CollaboratorAdoptionEntry): AdoptionStatus => {
    switch (entry.adoptionStatus) {
        case 'INSTALLED':
            return 'installed';
        case 'INVITED':
            return 'invited';
        case 'NO_WHATSAPP':
            return 'noWhatsApp';
        default:
            // An unknown status from a newer backend reads as "sin instalar": the admin
            // can still act on it, and nothing user-visible lies.
            return 'notInstalled';
    }
};

export const getAdoptionStatusLabel = (status: AdoptionStatus): string => {
    switch (status) {
        case 'installed':
            return 'Instalada'.localized();
        case 'invited':
            return 'Invitación enviada'.localized();
        case 'noWhatsApp':
            return 'Sin WhatsApp'.localized();
        default:
            return 'Sin instalar'.localized();
    }
};

/**
 * "Sin la app" covers both notInstalled and noWhatsApp on purpose: to the admin they
 * are one population ("les falta la app"), split only by whether the invite button works.
 */
export const matchesAdoptionStatusFilter = (
    status: AdoptionStatus,
    filter: AdoptionStatusFilter
): boolean => {
    switch (filter) {
        case 'withApp':
            return status === 'installed';
        case 'withoutApp':
            return status === 'notInstalled' || status === 'noWhatsApp';
        case 'invited':
            return status === 'invited';
        case 'all':
        default:
            return true;
    }
};

/** Rows without a device only appear under "Todas" — the platform pills describe devices. */
export const matchesAdoptionPlatformFilter = (
    platform: AdoptionPlatform | null,
    filter: AdoptionPlatformFilter
): boolean => {
    switch (filter) {
        case 'android':
            return platform === 'android';
        case 'ios':
            return platform === 'ios';
        case 'all':
        default:
            return true;
    }
};

/**
 * Whether an invitation can usefully be sent. Installed collaborators have nothing to
 * receive, and noWhatsApp ones nothing to receive it on. Invited rows stay invitable:
 * the backend reuses the pending token, so a re-send nudges without breaking the link.
 */
export const canInviteStatus = (status: AdoptionStatus): boolean =>
    status === 'notInstalled' || status === 'invited';

/** Same display formatting as the collaborators screens: no whatsapp: prefix, leading +. */
export const formatAdoptionPhone = (mobilePhone?: string): string => {
    const sanitized = (mobilePhone ?? '').replace(/^whatsapp:/i, '').trim();
    if (!sanitized) {
        return '';
    }
    return sanitized.startsWith('+') ? sanitized : `+${sanitized}`;
};

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * "vence en N días" for a live invitation; "vence hoy" inside the last day. Expired
 * invitations never reach this — the backend already reports them as NOT_INSTALLED.
 */
export const getInvitationExpiryLabel = (expiresAt?: number, now: number = Date.now()): string => {
    if (typeof expiresAt !== 'number' || expiresAt <= now) {
        return '';
    }
    const days = Math.floor((expiresAt - now) / MILLISECONDS_PER_DAY);
    if (days === 0) {
        return 'vence hoy'.localized();
    }
    if (days === 1) {
        return 'vence en 1 día'.localized();
    }
    return `${'vence en'.localized()} ${days} ${'días'.localized()}`;
};

const getAdoptionPlatform = (devicePlatform?: string): AdoptionPlatform | null => {
    const normalized = (devicePlatform ?? '').toLowerCase();
    if (normalized === 'android') {
        return 'android';
    }
    if (normalized === 'ios') {
        return 'ios';
    }
    return null;
};

const getPlatformLabel = (platform: AdoptionPlatform | null): string => {
    switch (platform) {
        case 'android':
            return 'Android';
        case 'ios':
            return 'iOS';
        default:
            return '';
    }
};

const formatTimestamp = (value: number | undefined, timezone?: string): string => {
    if (typeof value !== 'number' || value <= 0) {
        return '—';
    }
    return Description.Timestamp.timeAgoWithShortDate(value, timezone ?? undefined);
};

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

/**
 * Relative + absolute together ("3 días atrás · 29/8/2026, 09:12"). Inside the window
 * where timeAgoWithShortDate is already relative, the absolute half would duplicate it.
 */
const formatTimestampDetail = (value: number | undefined, timezone?: string): string => {
    if (typeof value !== 'number' || value <= 0) {
        return '—';
    }
    if (Date.now() - value <= TWO_HOURS_MS) {
        return Description.Timestamp.timeAgo(value);
    }
    return `${Description.Timestamp.timeAgo(value)} · ${Description.Timestamp.timeAgoWithShortDate(value, timezone ?? undefined)}`;
};

/** "Android 14" from a bare version number; the app's own label when it sends one. */
const getOSLabel = (platform: AdoptionPlatform | null, osVersion?: string): string => {
    const version = (osVersion ?? '').trim();
    if (!version) {
        return getPlatformLabel(platform);
    }
    return /^[\d.]+$/.test(version)
        ? `${getPlatformLabel(platform)} ${version}`.trim()
        : version;
};

export const normalizeAdoptionRow = (
    entry: CollaboratorAdoptionEntry,
    timezone?: string
): AppAdoptionTableRow => {
    const status = getAdoptionStatus(entry);
    const platform = getAdoptionPlatform(entry.devicePlatform);
    const phoneLabel = formatAdoptionPhone(entry.mobilePhone);
    const name = entry.name?.trim() || entry.email?.trim() || entry.userID;

    return {
        userID: entry.userID,
        name,
        imageURL: entry.imageURL || undefined,
        phoneLabel,
        empresa: entry.empresa?.trim() || '',
        status,
        statusLabel: getAdoptionStatusLabel(status),
        expiryLabel: status === 'invited' ? getInvitationExpiryLabel(entry.invitationExpiresAt) : '',
        platform,
        platformLabel: getPlatformLabel(platform),
        appVersionLabel: entry.appVersion ? `v${entry.appVersion}` : '',
        deviceModelLabel: entry.deviceModel?.trim() || '',
        osLabel: getOSLabel(platform, entry.osVersion),
        installedLabel: formatTimestamp(entry.installedAt, timezone),
        lastActivityLabel: formatTimestamp(entry.lastActivityAt, timezone),
        lastActivityDetailLabel: formatTimestampDetail(entry.lastActivityAt, timezone),
        canInvite: canInviteStatus(status),
        searchValue: [name, entry.email, phoneLabel, entry.userID, entry.empresa]
            .map((value) => (value ?? '').toLowerCase())
            .join(' '),
    };
};

export const countAdoptionRows = (rows: AppAdoptionTableRow[]): AdoptionCounts => ({
    total: rows.length,
    withApp: rows.filter((row) => row.status === 'installed').length,
    withoutApp: rows.filter((row) => row.status === 'notInstalled' || row.status === 'noWhatsApp').length,
    invited: rows.filter((row) => row.status === 'invited').length,
});

/**
 * Reports a bulk send honestly. The backend answers per user rather than failing the
 * batch, so a plain "listo" would hide the collaborators whose invitation never left —
 * the exact people the admin needs to chase.
 */
export const describeInvitationDispatch = (result: AppAdoptionInvitationsResult): SendInvitationsResult => {
    const sent = result.enqueued.length;
    const skipped = result.skipped.length;
    const failed = result.failed.length;

    if (sent === 0) {
        return {
            kind: 'error',
            message: 'No se pudo enviar ninguna invitación. Revisá que los colaboradores tengan WhatsApp registrado.'.localized(),
        };
    }
    if (skipped === 0 && failed === 0) {
        return {
            kind: 'success',
            message: sent === 1
                ? 'Invitación enviada por WhatsApp'.localized()
                : `${'Invitaciones enviadas por WhatsApp'.localized()} (${sent} ${'enviadas'.localized()})`,
        };
    }
    return {
        // Skipped means "already had the app" — good news, not a failure — so only
        // actual failures downgrade the result.
        kind: failed > 0 ? 'error' : 'success',
        message: `${'Invitaciones enviadas por WhatsApp'.localized()} (${sent} ${'enviadas'.localized()}, ${skipped + failed} ${'omitidas'.localized()})`,
    };
};

const SEARCH_ANALYTICS_DEBOUNCE_MS = 600;

interface UseAppAdoptionScreenViewModelProps {
    organizationID?: string;
    timezone?: string;
    appAdoptionService: AppAdoptionService;
}

/**
 * Adopción de la app: one row per active collaborator, filtered client-side.
 *
 * Filtering stays local because the list is the whole staff of one organization and the
 * stat cards / pills need instant counts. Selection, select-all and batch readiness live
 * here so the screen only binds checkboxes and buttons (AGENTS.md); actions return
 * structured results and the screen fires the toasts.
 */
export const useAppAdoptionScreenViewModel = ({
    organizationID,
    timezone,
    appAdoptionService,
}: UseAppAdoptionScreenViewModelProps) => {
    const [rows, setRows] = useState<AppAdoptionTableRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
    const [searchField, setSearchField] = useState('');
    const [statusFilter, setStatusFilterState] = useState<AdoptionStatusFilter>('all');
    const [platformFilter, setPlatformFilterState] = useState<AdoptionPlatformFilter>('all');
    const [selectedUserIDs, setSelectedUserIDs] = useState<string[]>([]);
    const [pendingInviteUserIDs, setPendingInviteUserIDs] = useState<string[] | null>(null);
    // Re-onboarding: the pending invite targets an INSTALLED collaborator whose app
    // was uninstalled manually — the backend must bypass the already-installed skip.
    const [pendingInviteIsReinstall, setPendingInviteIsReinstall] = useState(false);
    const [isSendingInvitations, setIsSendingInvitations] = useState(false);
    const [deviceDetailsRow, setDeviceDetailsRow] = useState<AppAdoptionTableRow | null>(null);
    const hasTrackedShownRef = useRef(false);
    const searchAnalyticsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasSearchedRef = useRef(false);

    const refresh = useCallback(async () => {
        if (!organizationID) {
            setRows([]);
            return;
        }

        setLoading(true);
        setLoadErrorMessage(null);

        try {
            const entries = await appAdoptionService.getCollaboratorAdoption(organizationID);
            const normalized = entries
                .map((entry) => normalizeAdoptionRow(entry, timezone))
                .sort((a, b) => a.name.localeCompare(b.name));

            setRows(normalized);
            // Selection survives a refresh, but only for rows still invitable — a
            // collaborator who installed the app in between silently drops out.
            const invitable = new Set(normalized.filter((row) => row.canInvite).map((row) => row.userID));
            setSelectedUserIDs((current) => current.filter((userID) => invitable.has(userID)));

            const counts = countAdoptionRows(normalized);
            const payload = {
                collaboratorCount: counts.total,
                installedCount: counts.withApp,
                withoutAppCount: counts.withoutApp,
                invitedCount: counts.invited,
            };
            if (hasTrackedShownRef.current) {
                AppAdoptionAnalyticsHelper.trackRefreshed(payload);
            } else {
                hasTrackedShownRef.current = true;
                AppAdoptionAnalyticsHelper.trackShown(payload);
            }
        } catch (error) {
            AppAdoptionAnalyticsHelper.trackLoadFailed();
            const message = error instanceof Error && error.message
                ? error.message
                : 'No se pudo cargar la adopción de la app. Intenta nuevamente.'.localized();

            setLoadErrorMessage(message);
        } finally {
            setLoading(false);
        }
    }, [appAdoptionService, organizationID, timezone]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const counts = useMemo(() => countAdoptionRows(rows), [rows]);

    const filteredRows = useMemo(() => {
        const normalizedSearch = searchField.trim().toLowerCase();
        return rows.filter((row) =>
            matchesAdoptionStatusFilter(row.status, statusFilter)
            && matchesAdoptionPlatformFilter(row.platform, platformFilter)
            && (!normalizedSearch || row.searchValue.includes(normalizedSearch))
        );
    }, [platformFilter, rows, searchField, statusFilter]);

    // Search analytics: debounced, metadata only — never the text itself (AGENTS.md).
    useEffect(() => {
        const trimmed = searchField.trim();
        if (!trimmed && !hasSearchedRef.current) {
            return;
        }
        hasSearchedRef.current = true;

        if (searchAnalyticsTimerRef.current) {
            clearTimeout(searchAnalyticsTimerRef.current);
        }
        searchAnalyticsTimerRef.current = setTimeout(() => {
            AppAdoptionAnalyticsHelper.trackSearched({
                queryLength: trimmed.length,
                hasQuery: trimmed.length > 0,
                resultCount: filteredRows.length,
                totalCount: rows.length,
            });
        }, SEARCH_ANALYTICS_DEBOUNCE_MS);
    }, [filteredRows.length, rows.length, searchField]);

    useEffect(() => {
        return () => {
            if (searchAnalyticsTimerRef.current) {
                clearTimeout(searchAnalyticsTimerRef.current);
            }
        };
    }, []);

    const invitableFilteredRows = useMemo(
        () => filteredRows.filter((row) => row.canInvite),
        [filteredRows]
    );

    const allFilteredSelected = useMemo(() => (
        invitableFilteredRows.length > 0
        && invitableFilteredRows.every((row) => selectedUserIDs.includes(row.userID))
    ), [invitableFilteredRows, selectedUserIDs]);

    const setStatusFilter = (filter: AdoptionStatusFilter) => {
        setStatusFilterState(filter);
        AppAdoptionAnalyticsHelper.trackFilterChanged({
            filter,
            resultCount: rows.filter((row) => matchesAdoptionStatusFilter(row.status, filter)).length,
        });
    };

    const setPlatformFilter = (filter: AdoptionPlatformFilter) => {
        setPlatformFilterState(filter);
        AppAdoptionAnalyticsHelper.trackFilterChanged({
            filter: `platform:${filter}`,
            resultCount: rows.filter((row) => matchesAdoptionPlatformFilter(row.platform, filter)).length,
        });
    };

    const toggleUser = (userID: string) => {
        setSelectedUserIDs((current) => current.includes(userID)
            ? current.filter((candidate) => candidate !== userID)
            : [...current, userID]);
    };

    /** Select-all is scoped to the rows the admin can currently see and act on. */
    const toggleSelectAll = () => {
        const visibleIDs = invitableFilteredRows.map((row) => row.userID);
        setSelectedUserIDs((current) => allFilteredSelected
            ? current.filter((userID) => !visibleIDs.includes(userID))
            : Array.from(new Set([...current, ...visibleIDs])));
    };

    const clearSelection = () => setSelectedUserIDs([]);

    const pendingInviteRows = useMemo(() => {
        if (!pendingInviteUserIDs) {
            return [];
        }
        return rows.filter((row) => pendingInviteUserIDs.includes(row.userID));
    }, [pendingInviteUserIDs, rows]);

    return {
        rows: filteredRows,
        allRows: rows,
        counts,
        loading,
        loadErrorMessage,
        searchField,
        setSearchField,
        statusFilter,
        setStatusFilter,
        platformFilter,
        setPlatformFilter,
        refresh,
        selectedUserIDs,
        selectedCount: selectedUserIDs.length,
        allFilteredSelected,
        hasInvitableFilteredRows: invitableFilteredRows.length > 0,
        toggleUser,
        toggleSelectAll,
        clearSelection,
        pendingInviteRows,
        isInviteModalOpen: pendingInviteUserIDs !== null,
        isSendingInvitations,
        deviceDetailsRow,
        isDeviceDetailsOpen: deviceDetailsRow !== null,
        openDeviceDetails: (row: AppAdoptionTableRow) => {
            AppAdoptionAnalyticsHelper.trackDeviceDetailsOpened();
            setDeviceDetailsRow(row);
        },
        closeDeviceDetails: () => setDeviceDetailsRow(null),
        /** Opens the confirmation modal for the current selection. */
        requestBulkInvite: () => {
            if (selectedUserIDs.length === 0) {
                return;
            }
            setPendingInviteIsReinstall(false);
            setPendingInviteUserIDs(selectedUserIDs);
        },
        /** Row-level invite and re-invite go through the same confirmation modal. */
        requestSingleInvite: (row: AppAdoptionTableRow) => {
            if (!row.canInvite) {
                return;
            }
            setPendingInviteIsReinstall(false);
            setPendingInviteUserIDs([row.userID]);
        },
        /**
         * Re-onboarding for an INSTALLED collaborator who uninstalled the app manually:
         * their installation record stays ACTIVE on the backend, so a normal invite is
         * skipped — this one bypasses the skip and redemption supersedes the stale record.
         */
        requestReinstallInvite: (row: AppAdoptionTableRow) => {
            setPendingInviteIsReinstall(true);
            setPendingInviteUserIDs([row.userID]);
        },
        /** First-use CTA: invite everyone an invitation can actually reach. */
        requestInviteAll: () => {
            const allInvitable = rows.filter((row) => row.canInvite).map((row) => row.userID);
            if (allInvitable.length === 0) {
                return;
            }
            setPendingInviteIsReinstall(false);
            setPendingInviteUserIDs(allInvitable);
        },
        cancelInvite: () => {
            if (isSendingInvitations) {
                return;
            }
            setPendingInviteUserIDs(null);
            setPendingInviteIsReinstall(false);
        },
        sendInvitations: async (): Promise<SendInvitationsResult> => {
            if (!organizationID) {
                return {
                    kind: 'error',
                    message: 'Selecciona una organización antes de invitar.'.localized(),
                };
            }
            if (!pendingInviteUserIDs || pendingInviteUserIDs.length === 0) {
                return {
                    kind: 'error',
                    message: 'Selecciona al menos un colaborador para invitar.'.localized(),
                };
            }

            setIsSendingInvitations(true);
            AppAdoptionAnalyticsHelper.trackInvitationsSubmitted({
                requested: pendingInviteUserIDs.length,
            });

            try {
                const result = await appAdoptionService.sendInvitations(
                    organizationID,
                    pendingInviteUserIDs,
                    { reinstall: pendingInviteIsReinstall }
                );
                AppAdoptionAnalyticsHelper.trackInvitationsSucceeded({
                    requested: pendingInviteUserIDs.length,
                    sent: result.enqueued.length,
                    skipped: result.skipped.length,
                    failed: result.failed.length,
                });

                setPendingInviteUserIDs(null);
                setPendingInviteIsReinstall(false);
                setSelectedUserIDs([]);
                await refresh();
                return describeInvitationDispatch(result);
            } catch (error) {
                AppAdoptionAnalyticsHelper.trackInvitationsFailed({
                    requested: pendingInviteUserIDs.length,
                });
                return {
                    kind: 'error',
                    message: error instanceof Error && error.message
                        ? error.message
                        : 'No se pudieron enviar las invitaciones. Intenta nuevamente.'.localized(),
                };
            } finally {
                setIsSendingInvitations(false);
            }
        },
    };
};
